import { Award, Clock } from 'lucide-react'
import Image from 'next/image'
import { ViewTransition } from 'react'
import { DiamondIcon } from '@/components/diamond/diamond-icon'
import { HomeLink } from '@/components/home-link'
import { Button } from '@/components/ui/button'
import { productImageTransitionName } from '@/features/products-catalog/view-transition'
import { formatCourseWorkloadLabel } from '@/helpers/active-workloads'
import { getProductType } from '@/helpers/product-type'
import starIcon from '@/public/icons/star.svg'
import type { CourseType } from '@/types/course-type'
import { EnumProductType } from '@/types/enum-product-type'

function getDestinationRoute(
  productType: string,
  productId: string,
  publicUrl: string
) {
  const ProductRouteMap: Record<
    string,
    (productId: string, publicUrl: string) => string
  > = {
    [EnumProductType.COURSE]: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    [EnumProductType.WEBINAR]: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    [EnumProductType.MENTORSHIP]: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    [EnumProductType.PERSONALIZED]: (productId, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
    [EnumProductType.POSTGRADUATE]: (productId, publicUrl) =>
      `/pos-graduacoes/info/${productId}`,
    [EnumProductType.TRACK]: (_, publicUrl) =>
      `/cursos/info/${productId}/${publicUrl}`,
  }

  const roulteBuilder = ProductRouteMap[productType.toLowerCase()]
  return roulteBuilder?.(productId, publicUrl)
}

interface ProductCardProps {
  product: CourseType
  /**
   * Nomeia a imagem para o view-transition card → info do produto. Só deve ser
   * ligado onde o mesmo produto aparece uma única vez na página (a listagem
   * /cursos): dois elementos com o mesmo nome quebram o morph.
   */
  hasSharedImageTransition?: boolean
}

export default function ProductCard({
  product,
  hasSharedImageTransition = false,
}: ProductCardProps) {
  const {
    name,
    product_type,
    image_url,
    description,
    workloads,
    external_id,
    reference,
    public_url,
    emission_type,
  } = product

  const routeDestination: string = getDestinationRoute(
    product_type,
    external_id ?? reference,
    public_url
  )

  if (!routeDestination) {
    console.error('Erro: A rota de destino não foi gerada corretamente.')
    return null
  }

  const productType: string = getProductType(product_type)
  const workloadLabel = formatCourseWorkloadLabel(workloads)

  const sessionMock = {
    user: {
      premium: false,
    },
  }

  const coursePremium = false

  return (
    <HomeLink
      className="group w-full"
      href={routeDestination}
      title={product.name}
    >
      <div className="relative w-full">
        <span className="absolute top-2 left-4 z-10 rounded bg-[#EAEEFD] p-1 font-medium text-gray-950 text-xs">
          {productType}
        </span>
        <div className="mb-4">
          <div className="relative overflow-hidden rounded-lg">
            <ViewTransition
              default="none"
              name={
                hasSharedImageTransition
                  ? productImageTransitionName(external_id ?? reference)
                  : undefined
              }
              share="morph"
            >
              <Image
                alt={name}
                className="h-42.25 w-full scale-100 rounded-lg object-cover filter transition duration-700 group-hover:scale-125 group-hover:brightness-50"
                height={180}
                src={image_url ?? '/img/product-fallback.webp'}
                width={280}
              />
            </ViewTransition>
            {coursePremium && (
              <div className="absolute top-3 right-3">
                <div className="flex max-h-[20px] max-w-[64px] items-center gap-[2px] rounded-sm border border-[#D4D4D4] bg-[#FAFAFA] p-1 shadow-md">
                  <DiamondIcon width={16} />

                  <span className="font-medium text-[10px] text-gray-800 leading-[100%]">
                    Diamond
                  </span>
                </div>
              </div>
            )}

            {emission_type === 'mec' && (
              <div className="absolute right-2 bottom-2 z-10 rounded-full">
                <Image
                  alt="e-MEC"
                  height={50}
                  src="/img/mec-seal.png"
                  width={50}
                />
              </div>
            )}
          </div>
        </div>
        <div className="mt-2">
          <div className="max-w-67.5 space-y-2">
            <h3 className="line-clamp-1 font-bold text-gray-900 text-lg leading-5">
              {name}
            </h3>
            <p className="line-clamp-3 font-normal text-base text-gray-500 leading-[17px]">
              {description}
            </p>
          </div>
          <div className="mt-2">
            <div className="flex items-center justify-between border-neutral-200 border-b pb-3">
              <div className="flex items-center">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Image
                    alt="Ícone de estrela"
                    height={16}
                    key={i}
                    src={starIcon}
                    width={16}
                  />
                ))}
              </div>
              {product_type && product_type !== 'track' && workloadLabel && (
                <div className="flex items-center gap-1">
                  <Clock className="size-4 text-gray-50" fill="#dfdfdf" />
                  <span className="font-normal text-base text-gray-900">
                    {workloadLabel}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-3 w-full">
              {coursePremium && !!sessionMock.user.premium && (
                <Button className="min-h-10 border border-primary-300 text-sm hover:bg-primary-300 hover:text-white">
                  Começar
                </Button>
              )}
              {coursePremium && !sessionMock.user.premium && (
                <Button className="min-h-10 border border-primary-300 text-sm hover:bg-primary-300 hover:text-white">
                  Assinar Premium <Award size={16} />
                </Button>
              )}
              {!coursePremium && (
                <Button className="min-h-10 w-full rounded-xl text-sm">
                  Fazer inscrição
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </HomeLink>
  )
}
